import { useState } from 'react';
import React from 'react';
import api from '../../../utils/API';
import { getMissionsActive, getDetails } from '../../../services/MissionService';
import { useLocation } from 'react-router-dom';

export const useCandidatsOffre = () => {
    const safeParse = (key, fallback) => {
        try {
            const val = sessionStorage.getItem(key);
            return val ? JSON.parse(val) : fallback;
        } catch { return fallback; }
    };

    const [filter, setFilter] = useState(() => sessionStorage.getItem('co_filter') || 'hybride');
    const [limit, setLimit] = useState(() => safeParse('co_limit', 20));
    const [experienceMin, setExperienceMin] = useState(() => safeParse('co_expMin', 0));
    const [experienceMax, setExperienceMax] = useState(() => safeParse('co_expMax', 20));
    const [seniorityLevel, setSeniorityLevel] = useState(() => sessionStorage.getItem('co_seniority') || 'Tous');
    const [selectedSkills, setSelectedSkills] = useState(() => safeParse('co_skills', []));
    const [selectedLanguages, setSelectedLanguages] = useState(() => safeParse('co_langs', []));
    const [selectedEducation, setSelectedEducation] = useState(() => sessionStorage.getItem('co_edu') || 'Tous');
    const [selectedLocation, setSelectedLocation] = useState(() => sessionStorage.getItem('co_loc') || '');
    const [jobUuid, setJobUuid] = useState(() => sessionStorage.getItem('co_jobUuid') || '');
    const [selectedMission, setSelectedMission] = useState(() => safeParse('co_mission', null));
    const [candidats, setCandidats] = useState([]);
    const [rawCandidats, setRawCandidats] = useState(() => safeParse('co_rawCandidats', []));
    const [loading, setLoading] = useState(false);
    const [rerankLoading, setRerankLoading] = useState(false);
    const [selectedCandidat, setSelectedCandidat] = useState(null);
    const [error, setError] = useState(null);
    const [shortlistLimit, setShortlistLimit] = useState(30);
    const [lancerAnchorEl, setLancerAnchorEl] = useState(null);
    const [rerankAnchorEl, setRerankAnchorEl] = useState(null);
    const [missions, setMissions] = useState([]);
    const [missionsLoading, setMissionsLoading] = useState(false);
    const [missionsError, setMissionsError] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [searchText, setSearchText] = useState(() => sessionStorage.getItem('co_searchText') || '');
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const [missionDetailsError, setMissionDetailsError] = useState(null);
    const location = useLocation();

    React.useEffect(() => {
        setRawCandidats(safeParse("co_rawCandidats", []));
        setJobUuid(sessionStorage.getItem("co_jobUuid") || "");
        setSelectedMission(safeParse("co_mission", null));
    }, [location.key]);

    React.useEffect(() => {
        sessionStorage.setItem('co_filter', filter);
        sessionStorage.setItem('co_limit', JSON.stringify(limit));
        sessionStorage.setItem('co_expMin', JSON.stringify(experienceMin));
        sessionStorage.setItem('co_expMax', JSON.stringify(experienceMax));
        sessionStorage.setItem('co_seniority', seniorityLevel);
        sessionStorage.setItem('co_skills', JSON.stringify(selectedSkills));
        sessionStorage.setItem('co_langs', JSON.stringify(selectedLanguages));
        sessionStorage.setItem('co_edu', selectedEducation);
        sessionStorage.setItem('co_loc', selectedLocation);
        sessionStorage.setItem('co_jobUuid', jobUuid);
        if (selectedMission) sessionStorage.setItem('co_mission', JSON.stringify(selectedMission));
        else sessionStorage.removeItem('co_mission');
        sessionStorage.setItem('co_rawCandidats', JSON.stringify(rawCandidats));
        sessionStorage.setItem('co_searchText', searchText);
    }, [filter, limit, experienceMin, experienceMax, seniorityLevel, selectedSkills, selectedLanguages, selectedEducation, selectedLocation, jobUuid, selectedMission, rawCandidats, searchText]);
    React.useEffect(() => {
        if (jobUuid && (!selectedMission || selectedMission.id !== jobUuid)) {
            setMissionDetailsError(null);
            getDetails(jobUuid)
                .then(({ data }) => {
                    setSelectedMission({
                        id: data.uuid,
                        value: data.nom_mission,
                        nom_societe: data.nom_societe,
                    });
                    setSearchText(data.nom_mission || '');
                })
                .catch((err) => {
                    const message = err.response?.data?.detail
                        || err.response?.data?.error
                        || err.message
                        || 'Erreur inconnue';
                    console.error(`Erreur lors de la récupération de la mission ${selectedMission?.value} :`, message);
                    setMissionDetailsError(message);
                });
        }
    }, [jobUuid]);
    React.useEffect(() => {
        let filtered = rawCandidats;
        if (experienceMin > 0 || experienceMax < 20) {
            filtered = filtered.filter(c => {
                const exp = c.properties?.years_of_experience || 0;
                return exp >= experienceMin && exp <= experienceMax;
            });
        }
        if (seniorityLevel !== 'Tous') {
            filtered = filtered.filter(c => {
                const all = [...(c.properties?.seniority_technologies || []), ...(c.properties?.seniority_programming_languages || [])];
                return all.length > 0 && all.some(item => (item.level || '').toLowerCase() === seniorityLevel.toLowerCase());
            });
        }
        if (selectedSkills.length > 0) {
            filtered = filtered.filter(c => {
                const all = [...(c.properties?.technical_skills || []), ...(c.properties?.programming_languages || [])].map(s => typeof s === 'string' ? s.toLowerCase() : '');
                return selectedSkills.every(skill => all.includes(skill.toLowerCase()));
            });
        }
        if (selectedLanguages.length > 0) {
            filtered = filtered.filter(c => {
                const langs = (c.properties?.spoken_languages || []).map(l => typeof l === 'string' ? l.toLowerCase() : '');
                return selectedLanguages.every(lang => langs.includes(lang.toLowerCase()));
            });
        }
        if (selectedEducation && selectedEducation !== 'Tous') {
            filtered = filtered.filter(c => c.properties?.education_level === selectedEducation);
        }
        if (selectedLocation && selectedLocation.trim() !== '') {
            filtered = filtered.filter(c => (c.properties?.location || '').toLowerCase().includes(selectedLocation.toLowerCase()));
        }
        setCandidats(filtered);
        setSelectedCandidat(prev => (prev && !filtered.find(c => c.id === prev.id) ? null : prev));
    }, [rawCandidats, experienceMin, experienceMax, seniorityLevel, selectedSkills, selectedLanguages, selectedEducation, selectedLocation]);

    const availableSkills = Array.from(new Set(rawCandidats.flatMap(c => [...(c.properties?.technical_skills || []), ...(c.properties?.programming_languages || [])]).filter(Boolean))).sort();
    const availableLanguages = Array.from(new Set(rawCandidats.flatMap(c => c.properties?.spoken_languages || []).filter(Boolean))).sort();
    const availableEducation = ['Tous', ...Array.from(new Set(rawCandidats.map(c => c.properties?.education_level).filter(Boolean))).sort()];
    const availableSeniorities = ['Tous', ...Array.from(new Set(rawCandidats.flatMap(c => {
        const ts = c.properties?.seniority_technologies || [];
        const ls = c.properties?.seniority_programming_languages || [];
        return [...ts, ...ls].map(t => t.level ? (t.level.charAt(0).toUpperCase() + t.level.slice(1).toLowerCase()) : null).filter(Boolean);
    })))];
    const minCandidateExp = rawCandidats.length > 0 ? Math.min(...rawCandidats.map(c => c.properties?.years_of_experience || 0)) : 0;
    const maxCandidateExp = rawCandidats.length > 0 ? Math.max(...rawCandidats.map(c => c.properties?.years_of_experience || 0)) : 20;
    const filteredMissions = missions.filter(m => (m.value || m.nom_societe || '').toLowerCase().includes(searchText.toLowerCase()));

    const loadMissions = async () => {
        if (missions.length > 0) return;
        setMissionsLoading(true); setMissionsError(null);
        try {
            const response = await getMissionsActive();
            let data = [];
            if (Array.isArray(response.data)) data = response.data;
            else if (Array.isArray(response.data?.data)) data = response.data.data;
            else if (Array.isArray(response.data?.results)) data = response.data.results;
            setMissions(data);
        } catch (e) {
            setMissionsError(e.response?.data?.detail || e.response?.data?.error || e.message || 'Erreur inconnue');
        } finally { setMissionsLoading(false); }
    };

    const handleSelectMission = (mission) => {
        setSelectedMission(mission); setJobUuid(mission.id);
        setSearchText(mission.value || ''); setDropdownOpen(false);
        setError(null); setRawCandidats([]); setSelectedCandidat(null);
    };

    const handleClearSearch = () => {
        setSearchText(''); setJobUuid(''); setSelectedMission(null);
        setRawCandidats([]); setError(null);
    };

    const lancerLaRecherche = async () => {
        setLancerAnchorEl(null);

        if (!jobUuid) { setError("Veuillez sélectionner une mission avant de lancer la recherche."); return; }
        setLoading(true); setError(null);
        try {
            const response = await api.post('/mission/candidates', { job_uuid: jobUuid, mode: filter, limit });
            const list = Array.isArray(response.data.data || response.data.results || response.data) ? (response.data.data || response.data.results || response.data) : [];
            setRawCandidats(list); setSelectedCandidat(null);
            if (list.length === 0) setError("Aucun candidat trouvé pour cette mission.");
        } catch (err) {
            let errorMsg = err.response?.data?.detail || err.response?.data?.error || err.message || 'Erreur serveur inconnue';
            if (typeof errorMsg === 'string' && errorMsg.toLowerCase().includes('no job found')) {
                errorMsg = `Offre introuvable sous le nom "${selectedMission?.value || 'sélectionnée'}"`;
            }
            setError(`Erreur : ${errorMsg}`);
            setRawCandidats([]);
        } finally { setLoading(false); }
    };

    const lancerLeReranking = async () => {
        setRerankAnchorEl(null);
        if (!jobUuid) { setError("Veuillez sélectionner une mission avant de lancer le reranking."); return; }
        setRerankLoading(true); setError(null);
        try {
            const response = await api.post('/mission/candidates/rerank', { job_uuid: jobUuid, mode: filter, limit, shortlist_limit: shortlistLimit });
            const list = Array.isArray(response.data.data || response.data.results || response.data) ? (response.data.data || response.data.results || response.data) : [];
            setRawCandidats(list); setSelectedCandidat(null);
            if (list.length === 0) setError("Aucun candidat trouvé après reranking pour cette mission.");
        } catch (err) {
            let errorMsg = err.response?.data?.detail || err.response?.data?.error || err.message || 'Erreur serveur inconnue';
            if (typeof errorMsg === 'string' && errorMsg.toLowerCase().includes('no job found')) {
                errorMsg = `Offre introuvable sous le nom "${selectedMission?.value || 'sélectionnée'}"`;
            }
            setError(`Erreur : ${errorMsg}`);
            setRawCandidats([]);
        } finally { setRerankLoading(false); }
    };

    return {

        filter, setFilter, limit, setLimit, candidats, rawCandidats,
        loading, rerankLoading, selectedCandidat, setSelectedCandidat,
        error, setError, shortlistLimit, setShortlistLimit,
        lancerAnchorEl, setLancerAnchorEl, rerankAnchorEl, setRerankAnchorEl,
        missions, missionsLoading, missionsError, dropdownOpen, setDropdownOpen,
        searchText, setSearchText, filterAnchorEl, setFilterAnchorEl,
        selectedMission, jobUuid,

        availableSkills, availableLanguages, availableEducation, availableSeniorities,
        minCandidateExp, maxCandidateExp, filteredMissions,

        experienceMin, setExperienceMin, experienceMax, setExperienceMax,
        seniorityLevel, setSeniorityLevel, selectedSkills, setSelectedSkills,
        selectedLanguages, setSelectedLanguages, selectedEducation, setSelectedEducation,
        selectedLocation, setSelectedLocation,
        loadMissions, handleSelectMission, handleClearSearch,
        lancerLaRecherche, lancerLeReranking,
    };
};
